import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getComponentById, updateComponent, createComponent } from '../api/componentService';
import CodeEditor from '../components/molecules/CodeEditor';
import CodePreviewPanel from '../components/molecules/CodePreviewPanel';
import FormField from '../components/molecules/FormField';
import Button from '../components/atoms/Button';
import Text from '../components/atoms/Text';
import Badge from '../components/atoms/Badge';
import { CATEGORIES } from '../utils/constants';
import { getUserRole } from '../utils/auth';
import { useToast } from '../components/atoms/Toast';

/**
 * Component Editor Page
 * Handles creating and editing components with live preview
 */
const ComponentEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const isAdmin = getUserRole() === 'ADMIN';
  const params = new URLSearchParams(window.location.search);
  const toast = useToast();

  // Component state
  const [component, setComponent] = useState({
    title: '',
    description: '',
    category: '',
    tags: '',
    htmlCode: '',
    cssCode: '',
    jsCode: '',
  visibility: isAdmin ? (params.get('private') ? 'private' : 'public') : 'private'
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  // Load component data if editing
  const loadComponent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getComponentById(id);
      setComponent({
        title: response.data.title || '',
        description: response.data.description || '',
        category: response.data.category || '',
        tags: Array.isArray(response.data.tags) ? response.data.tags.join(', ') : '',
        htmlCode: response.data.htmlCode || '',
        cssCode: response.data.cssCode || '',
        jsCode: response.data.jsCode || '',
        visibility: response.data.visibility || 'public'
      });
      setError(null);
    } catch (err) {
      setError('Failed to load component. Please try again.');
      console.error('Error loading component:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditMode) {
      loadComponent();
    }
  }, [isEditMode, loadComponent]);

  const handleFieldChange = (field, value) => {
    setComponent(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!component.title.trim()) {
      setError('Component title is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const componentData = {
        ...component,
        tags: component.tags ? component.tags.split(',').map(tag => tag.trim()).filter(Boolean) : []
      };
  // Users can only create private components; admins can choose
  if (!isAdmin) componentData.visibility = 'private';

      if (isEditMode) {
        await updateComponent(id, componentData);
        toast?.success?.('Component updated');
      } else {
        await createComponent(componentData);
        toast?.success?.('Component created');
      }

      setIsDirty(false);
      navigate('/components');
    } catch (err) {
  setError(`Failed to ${isEditMode ? 'update' : 'create'} component. Please try again.`);
  toast?.error?.(`Failed to ${isEditMode ? 'update' : 'create'} component`);
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} component:`, err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
      return;
    }
    navigate('/components');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <Text>Loading component...</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <Text variant="h1" className="text-gray-900 mb-2">
                {isEditMode ? 'Edit Component' : 'Create New Component'}
              </Text>
              <Text variant="body" className="text-gray-600">
                {isEditMode ? 'Modify your component and see changes in real-time' : 'Build your component with live preview'}
              </Text>
            </div>
            <div className="flex items-center gap-3">
              {isDirty && (
                <Badge variant="blue" size="sm">
                  Unsaved changes
                </Badge>
              )}
              <Button
                variant="default"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || !component.title.trim()}
                leftIcon={saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : null}
              >
                {saving ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
              </Button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <Text variant="small" className="text-red-700">
              {error}
            </Text>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Component Details and Code Editor */}
          <div className="space-y-6">
            {/* Component Details */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <Text variant="h3" className="text-gray-900 mb-4">
                Component Details
              </Text>
              
              <div className="space-y-4">
                {/* Visibility: only admins can set public; users default to private */}
                {isAdmin && (
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="visibility"
                        checked={component.visibility === 'public'}
                        onChange={() => handleFieldChange('visibility', 'public')}
                      />
                      <span className="text-sm text-gray-700">Public</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="visibility"
                        checked={component.visibility === 'private'}
                        onChange={() => handleFieldChange('visibility', 'private')}
                      />
                      <span className="text-sm text-gray-700">Private</span>
                    </label>
                  </div>
                )}
                <FormField
                  label="Title"
                  value={component.title}
                  onChange={(value) => handleFieldChange('title', value)}
                  placeholder="Enter component title"
                  required
                />

                <FormField
                  label="Description"
                  value={component.description}
                  onChange={(value) => handleFieldChange('description', value)}
                  placeholder="Brief description of the component"
                  helpText="Optional description to help others understand this component"
                />

                <FormField label="Category" helpText="Select the component category">
                  <select
                    className="w-full border rounded-lg px-3 py-2 bg-white text-gray-900"
                    value={component.category}
                    onChange={(e) => handleFieldChange('category', e.target.value)}
                  >
                    <option value="">Select a category</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </FormField>

                <FormField
                  label="Tags"
                  value={component.tags}
                  onChange={(value) => handleFieldChange('tags', value)}
                  placeholder="react, button, interactive (comma separated)"
                  helpText="Comma-separated tags for easier searching"
                />
              </div>
            </div>

            {/* Code Editor */}
            <CodeEditor
              htmlCode={component.htmlCode}
              cssCode={component.cssCode}
              jsCode={component.jsCode}
              onHtmlChange={(value) => handleFieldChange('htmlCode', value)}
              onCssChange={(value) => handleFieldChange('cssCode', value)}
              onJsChange={(value) => handleFieldChange('jsCode', value)}
            />
          </div>

          {/* Right Column - Live Preview */}
          <div className="space-y-6">
            <CodePreviewPanel
              htmlCode={component.htmlCode}
              cssCode={component.cssCode}
              jsCode={component.jsCode}
              title={component.title || 'Component Preview'}
            />

            {/* Preview Instructions */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <Text variant="h4" className="text-blue-900 mb-2">
                Live Preview
              </Text>
              <Text variant="small" className="text-blue-700">
                Your component will update in real-time as you type. Use the fullscreen button for a better view of your component.
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentEditor;
